import { useState } from "react";
import { ArrowDown, ArrowUp, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { fieldLabel } from "./DiaryTrendChart";
import { DiaryAddQuestion, TYPE_LABEL } from "./DiaryAddQuestion";

const GROUPS = [["entry_fields", "Each activity"], ["followup_fields", "Next-day questions"], ["day_fields", "Daily notes"]];

function QuestionRow({ f, i, n, readOnly, onField, onMove, onRemove }) {
  const tid = `diary-editor-field-${f.code}`;
  return (
    <div className="rounded-md border border-zinc-200 p-2.5" data-testid={tid}>
      <div className="mb-2 flex items-center gap-2">
        <span className="rounded bg-zinc-100 px-1.5 py-0.5 text-2xs font-semibold uppercase text-zinc-600" data-testid={`${tid}-type`}>{TYPE_LABEL[f.type] || f.type}</span>
        {f.required && <span className="text-2xs font-semibold uppercase text-zinc-500">required</span>}
        {!readOnly && (
          <div className="ml-auto flex gap-1">
            <Button size="icon" variant="ghost" className="h-7 w-7" disabled={i === 0} onClick={() => onMove(i, -1)} title="Move up" data-testid={`${tid}-up`}><ArrowUp className="h-3.5 w-3.5" /></Button>
            <Button size="icon" variant="ghost" className="h-7 w-7" disabled={i === n - 1} onClick={() => onMove(i, 1)} title="Move down" data-testid={`${tid}-down`}><ArrowDown className="h-3.5 w-3.5" /></Button>
            <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600 hover:text-red-700" onClick={() => onRemove(i)} title="Remove question" data-testid={`${tid}-remove`}><Trash2 className="h-3.5 w-3.5" /></Button>
          </div>
        )}
      </div>
      <div className="grid gap-2 sm:grid-cols-3">
        <div><Label className="text-xs">Short label</Label>
          <Input className="mt-1 h-8" value={f.label || ""} placeholder={fieldLabel(f)} readOnly={readOnly} onChange={(e) => onField(i, "label", e.target.value)} data-testid={`${tid}-label`} /></div>
        <div><Label className="text-xs">Question</Label>
          <Input className="mt-1 h-8" value={f.prompt || ""} readOnly={readOnly} onChange={(e) => onField(i, "prompt", e.target.value)} data-testid={`${tid}-prompt`} /></div>
        <div><Label className="text-xs">Help text (optional)</Label>
          <Input className="mt-1 h-8" value={f.help || ""} readOnly={readOnly} onChange={(e) => onField(i, "help", e.target.value)} data-testid={`${tid}-help`} /></div>
      </div>
    </div>
  );
}

/** Add, reword, reorder and remove diary questions; removing one also removes the flag rules that use it. */
export function DiaryQuestions({ d, readOnly, setDiary }) {
  const [adding, setAdding] = useState(null);
  const taken = new Set(GROUPS.flatMap(([g]) => (d[g] || []).map((f) => f.code)));
  const setGroup = (g, list, extra = {}) => setDiary({ ...d, [g]: list, ...extra });
  const onField = (g) => (i, key, val) => setGroup(g, d[g].map((f, k) => (k === i ? { ...f, [key]: val } : f)));
  const onMove = (g) => (i, step) => { const l = [...d[g]]; [l[i], l[i + step]] = [l[i + step], l[i]]; setGroup(g, l); };
  const onRemove = (g) => (i) => {
    const f = d[g][i];
    const rules = (d.alert_rules || []).filter((r) => r.when?.field === f.code);
    const msg = `Remove “${fieldLabel(f)}” from this draft?${rules.length ? ` Its ${rules.length} flag rule(s) will be removed too.` : ""} Earlier diaries keep their answers.`;
    if (!window.confirm(msg)) return;
    setGroup(g, d[g].filter((_, k) => k !== i), { alert_rules: (d.alert_rules || []).filter((r) => r.when?.field !== f.code) });
  };
  return GROUPS.map(([g, title]) => (
    <div key={g} className="space-y-2" data-testid={`diary-editor-group-${g}`}>
      <div className="flex items-center gap-2">
        <div className="text-2xs font-semibold uppercase tracking-[0.15em] text-zinc-500">{title}</div>
        {!readOnly && adding !== g && (
          <Button size="sm" variant="ghost" className="ml-auto h-7" onClick={() => setAdding(g)} data-testid={`diary-add-${g}-btn`}><Plus className="mr-1 h-3.5 w-3.5" />Add question</Button>
        )}
      </div>
      {(d[g] || []).map((f, i) => <QuestionRow key={f.code} f={f} i={i} n={d[g].length} readOnly={readOnly} onField={onField(g)} onMove={onMove(g)} onRemove={onRemove(g)} />)}
      {!(d[g] || []).length && <p className="text-xs text-zinc-400">No questions in this group.</p>}
      {adding === g && <DiaryAddQuestion group={g} taken={taken} onCancel={() => setAdding(null)}
                                         onAdd={(f) => { setGroup(g, [...(d[g] || []), f]); setAdding(null); }} />}
    </div>
  ));
}
